import axios from 'axios';
import {
  ApiResponse,
  TenantApplicationAccess,
  CreateTenantAccessRequest,
  ApiKey,
  CreateApiKeyRequest,
  Assessment,
  AssessmentConfiguration,
  CreateAssessmentResponse,
  FormSubmissionResult,
  AssessmentFormData
} from '../types';
import {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  TokenRefreshRequest,
  TokenRefreshResponse,
  User,
  ChangePasswordRequest,
  UserUpdateRequest,
  ApiKeyCreate,
  ApiKeyCreateResponse,
  ApiKeyResponse,
  ApiKeyListResponse,
  UserProfileResponse,
} from '../types/auth';

type AxiosResponse<T = any> = {
  data: T;
  status: number;
  statusText: string;
  headers: any;
  config: any;
  request?: any;
};

// API Configuration
// Use REACT_APP_API_BASE_URL which already includes /api/v1 (consistent with config/api.ts)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// ============================================================================
// TOKEN MANAGEMENT
// ============================================================================

const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user_data';

export const tokenService = {
  getAccessToken: (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
  },

  setAccessToken: (token: string): void => {
    localStorage.setItem(TOKEN_KEY, token);
  },

  getRefreshToken: (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  setRefreshToken: (token: string): void => {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  },

  getUser: (): User | null => {
    const userData = localStorage.getItem(USER_KEY);
    return userData ? JSON.parse(userData) : null;
  },

  setUser: (user: User): void => {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  clearTokens: (): void => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },

  setTokens: (accessToken: string, refreshToken: string, user: User): void => {
    tokenService.setAccessToken(accessToken);
    tokenService.setRefreshToken(refreshToken);
    tokenService.setUser(user);
  }
};

// ============================================================================
// REQUEST INTERCEPTOR (Add JWT token to requests)
// ============================================================================

apiClient.interceptors.request.use(
  (config: any) => {
    console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);

    // Add JWT token to Authorization header if available
    const token = tokenService.getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// ============================================================================
// RESPONSE INTERCEPTOR (Handle token refresh)
// ============================================================================

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

apiClient.interceptors.response.use(
  (response) => {
    console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  async (error: any) => {
    const originalRequest = error.config as any & { _retry?: boolean };

    console.error('❌ API Response Error:', error.response?.data || error.message);

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Avoid refresh on auth endpoints
      if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/register')) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        const refreshToken = tokenService.getRefreshToken();

        if (!refreshToken) {
          // No refresh token, redirect to login
          tokenService.clearTokens();
          window.location.href = '/login';
          return Promise.reject(error);
        }

        try {
          // Attempt to refresh token
          const response = await axios.post<TokenRefreshResponse>(
            `${API_BASE_URL}/auth/refresh`,
            { refresh_token: refreshToken }
          );

          const { access_token } = response.data;
          tokenService.setAccessToken(access_token);

          isRefreshing = false;
          onTokenRefreshed(access_token);

          // Retry original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
          }
          return apiClient(originalRequest);

        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect to login
          isRefreshing = false;
          tokenService.clearTokens();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // Token refresh in progress, wait for it
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            resolve(apiClient(originalRequest));
          });
        });
      }
    }

    return Promise.reject(error);
  }
);

// Tenant Application Access API
export const tenantAccessApi = {
  create: async (data: CreateTenantAccessRequest): Promise<TenantApplicationAccess> => {
    const response: AxiosResponse<ApiResponse<TenantApplicationAccess>> = await apiClient.post(
      '/language-tests/tenant-access',
      data
    );
    return response.data.data!;
  },

  list: async (): Promise<TenantApplicationAccess[]> => {
    const response: AxiosResponse<ApiResponse<TenantApplicationAccess[]>> = await apiClient.get(
      '/language-tests/tenant-access'
    );
    return response.data.data!;
  },

  getById: async (id: string): Promise<TenantApplicationAccess> => {
    const response: AxiosResponse<ApiResponse<TenantApplicationAccess>> = await apiClient.get(
      `/language-tests/tenant-access/${id}`
    );
    return response.data.data!;
  }
};

// API Key API
export const apiKeyApi = {
  create: async (data: CreateApiKeyRequest): Promise<ApiKey & { key: string }> => {
    const response: AxiosResponse<ApiResponse<ApiKey & { key: string }>> = await apiClient.post(
      '/language-tests/api-keys',
      data
    );
    return response.data.data!;
  },

  list: async (): Promise<ApiKey[]> => {
    const response: AxiosResponse<ApiResponse<ApiKey[]>> = await apiClient.get(
      '/language-tests/api-keys'
    );
    return response.data.data!;
  }
};

// Assessment API
export const assessmentApi = {
  create: async (data: {
    name: string;
    language_id: number;
    configuration: AssessmentConfiguration;
    data?: Record<string, any>;
  }): Promise<Assessment> => {
    const response: AxiosResponse<ApiResponse<Assessment>> = await apiClient.post(
      '/language-tests/assessments',
      data
    );
    return response.data.data!;
  },

  list: async (): Promise<Assessment[]> => {
    const response: AxiosResponse<ApiResponse<Assessment[]>> = await apiClient.get(
      '/language-tests/assessments'
    );
    return response.data.data!;
  },

  getById: async (id: string): Promise<Assessment> => {
    const response: AxiosResponse<ApiResponse<Assessment>> = await apiClient.get(
      `/language-tests/assessments/${id}`
    );
    return response.data.data!;
  }
};

// Combined Service for Form Submission
export const assessmentFormService = {
  submitForm: async (formData: AssessmentFormData): Promise<FormSubmissionResult> => {
    try {
      console.log('📝 Starting form submission process...');
      
      // Step 1: Create Tenant Application Access
      console.log('1️⃣ Creating tenant application access...');
      const tenantAccessData: CreateTenantAccessRequest = {
        tenant_id: formData.tenant_id,
        application_name: formData.application_name,
        access_level: formData.access_level,
        permissions: formData.permissions,
        expires_at: undefined // Optional
      };
      
      const tenantAccess = await tenantAccessApi.create(tenantAccessData);
      console.log('✅ Tenant access created:', tenantAccess.id);

      // Step 2: Create API Key
      console.log('2️⃣ Creating API key...');
      const apiKeyData: CreateApiKeyRequest = {
        application_access_id: tenantAccess.id,
        tenant_id: formData.tenant_id,
        description: formData.api_key_description,
        created_by: formData.created_by,
        expires_at: formData.api_key_expires_at
      };
      
      const apiKey = await apiKeyApi.create(apiKeyData);
      console.log('✅ API key created:', apiKey.key_prefix);

      // Step 3: Create Assessment Configuration
      console.log('3️⃣ Creating assessment...');
      const assessmentConfig: AssessmentConfiguration = {
        total_duration_minutes: formData.total_duration_minutes,
        total_score: formData.total_score,
        sections: [
          {
            section_name: "Listening",
            duration_minutes: formData.listening_duration,
            total_score: formData.listening_score,
            question_selection: {
              listening_comprehension_sets: formData.listening_comprehension_sets,
              difficulty_level: "intermediate",
              audio_required: true
            }
          },
          {
            section_name: "Reading",
            duration_minutes: formData.reading_duration,
            total_score: formData.reading_score,
            question_selection: {
              reading_comprehension_sets: formData.reading_comprehension_sets,
              reading_fluency_individual: formData.reading_fluency_individual,
              difficulty_level: "intermediate"
            }
          },
          {
            section_name: "Speaking",
            duration_minutes: formData.speaking_duration,
            total_score: formData.speaking_score,
            question_selection: {
              personal_background: formData.personal_background,
              opinion_reasoning: formData.opinion_reasoning,
              problem_solving: formData.problem_solving,
              future_planning: formData.future_planning
            }
          }
        ]
      };

      const assessmentData = {
        name: formData.assessment_name,
        language_id: formData.language_id,
        configuration: assessmentConfig,
        data: {
          created_via: "assessment_portal",
          tenant_id: formData.tenant_id,
          api_key_id: apiKey.id
        }
      };
      
      const assessment = await assessmentApi.create(assessmentData);
      console.log('✅ Assessment created:', assessment.id);

      // Return combined result
      const result: CreateAssessmentResponse = {
        tenant_access: tenantAccess,
        api_key: apiKey,
        assessment: assessment
      };

      console.log('🎉 Form submission completed successfully!');
      return {
        success: true,
        data: result
      };

    } catch (error: any) {
      console.error('❌ Form submission failed:', error);
      
      let errorMessage = 'An unexpected error occurred';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }
};

// Health Check
export const healthApi = {
  check: async (): Promise<{ status: string; timestamp: string }> => {
    const response = await apiClient.get('/health');
    return response.data as { status: string; timestamp: string };
  }
};

// ============================================================================
// AUTHENTICATION API
// ============================================================================

export const authApi = {
  /**
   * Register a new user
   */
  register: async (data: RegisterRequest): Promise<RegisterResponse> => {
    const response = await apiClient.post<RegisterResponse>('/auth/register', data);
    return response.data;
  },

  /**
   * Login user and get JWT tokens
   */
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/login', data);
    const { access_token, refresh_token, user } = response.data;

    // Store tokens and user data
    tokenService.setTokens(access_token, refresh_token, user);

    return response.data;
  },

  /**
   * Logout user (revoke refresh token)
   */
  logout: async (): Promise<void> => {
    const refreshToken = tokenService.getRefreshToken();
    try {
      await apiClient.post('/auth/logout', { refresh_token: refreshToken });
    } finally {
      // Clear tokens even if API call fails
      tokenService.clearTokens();
    }
  },

  /**
   * Refresh access token
   */
  refreshToken: async (): Promise<TokenRefreshResponse> => {
    const refreshToken = tokenService.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await apiClient.post<TokenRefreshResponse>('/auth/refresh', {
      refresh_token: refreshToken
    });

    tokenService.setAccessToken(response.data.access_token);
    return response.data;
  },

  /**
   * Get current user profile
   */
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<User>('/auth/me');
    return response.data;
  },

  /**
   * Verify if token is valid
   */
  verifyToken: async (): Promise<boolean> => {
    try {
      await apiClient.post('/auth/verify-token');
      return true;
    } catch (error) {
      return false;
    }
  }
};

// ============================================================================
// USER PROFILE API
// ============================================================================

export const userApi = {
  /**
   * Get user profile with stats
   */
  getProfile: async (): Promise<UserProfileResponse> => {
    const response = await apiClient.get<UserProfileResponse>('/users/me');
    return response.data;
  },

  /**
   * Update user profile
   */
  updateProfile: async (data: UserUpdateRequest): Promise<User> => {
    const response = await apiClient.put<User>('/users/me', data);

    // Update stored user data
    tokenService.setUser(response.data);

    return response.data;
  },

  /**
   * Change user password
   */
  changePassword: async (data: ChangePasswordRequest): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>('/users/me/change-password', data);
    return response.data;
  },

  /**
   * Delete user account
   */
  deleteAccount: async (): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ message: string }>('/users/me');
    tokenService.clearTokens();
    return response.data;
  }
};

// ============================================================================
// API KEY MANAGEMENT API
// ============================================================================

export const userApiKeyApi = {
  /**
   * Create new API key for current user
   */
  create: async (data: ApiKeyCreate): Promise<ApiKeyCreateResponse> => {
    const response = await apiClient.post<ApiKeyCreateResponse>('/api-keys', data);
    return response.data;
  },

  /**
   * List all API keys for current user
   */
  list: async (): Promise<ApiKeyListResponse> => {
    const response = await apiClient.get<ApiKeyListResponse>('/api-keys');
    return response.data;
  },

  /**
   * Get specific API key details
   */
  getById: async (keyId: string): Promise<ApiKeyResponse> => {
    const response = await apiClient.get<ApiKeyResponse>(`/api-keys/${keyId}`);
    return response.data;
  },

  /**
   * Update API key
   */
  update: async (keyId: string, data: Partial<ApiKeyCreate & { is_active: boolean }>): Promise<ApiKeyResponse> => {
    const response = await apiClient.patch<ApiKeyResponse>(`/api-keys/${keyId}`, data);
    return response.data;
  },

  /**
   * Delete/revoke API key
   */
  delete: async (keyId: string): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ message: string }>(`/api-keys/${keyId}`);
    return response.data;
  },

  /**
   * Regenerate API key
   */
  regenerate: async (keyId: string): Promise<ApiKeyCreateResponse> => {
    const response = await apiClient.post<ApiKeyCreateResponse>(`/api-keys/${keyId}/regenerate`);
    return response.data;
  }
};

export default apiClient;
